/**
 * 零注册身份：第一次打开就在后台匿名注册，**永远不出现注册表单**。
 *
 * 三件事：
 *   1. 进程内维护 `AuthSession` 单例（`auth/session.ts` 已实现单飞刷新），
 *      接线层和大厅共用同一个，否则两条路各自注册会拿到两个不同的 accountId；
 *   2. 挂载时 `ensureAccessToken()` —— 没凭据就匿名注册，有就刷新。失败可重试，
 *      期间界面照常可用（按钮 disabled + 一行说明），绝不弹模态挡路；
 *   3. 昵称：本地 localStorage 一份（默认随机生成「牌手8823」），账号上一份。
 *      建房/进房时发出去的是本地那份（`createRoom` / `joinRoom` 的 nickname 字段），
 *      两份怎么对齐见 `decideNickname` 与 `nicknameSync.ts`。
 *
 * ★ 红线 #15 的延伸：随机昵称也走 `crypto.getRandomValues`。虽然昵称不影响公平性，
 *   但代码里出现 `Math.random` 就会被后来的人复制到洗牌相关的地方去。
 */
import { useCallback, useEffect, useState } from 'react'
import { create } from 'zustand'
import type { PublicAccount } from '@poker/shared'

import {
  createAuthSession,
  type AuthChange,
  type AuthSession,
  type AuthSessionOptions,
  type StorageLike,
} from '../auth/session.js'
// ★ 与 nicknameSync → profileApi → useAuth 成环；两边都只在函数体里用对方，模块求值时不碰
import { watchAccountNickname } from './nicknameSync.js'

export const NICKNAME_KEY = 'poker.nickname.v1'
/**
 * 「本地这个名字用户改过，还没确认存到账号上」。有它时本地为准（刷新后补存），没有时账号为准。
 *
 * ★ 以前用「用户是否亲手改过」来判断听谁的，但匿名注册一完成就会把它置成 true，于是
 *   登录后账号上的真名覆盖不上来，别的设备改的名字也同步不过来 —— 判据必须是「有没有没存上的改动」。
 */
export const NICKNAME_DIRTY_KEY = 'poker.nickname.dirty.v1'
/** 本地名字最后一次和哪个账号对齐过。与当前账号不同 = 换了身份（登录 / 凭据作废后重新注册）。 */
export const NICKNAME_OWNER_KEY = 'poker.nickname.owner.v1'
/** 与服务端 `ws/schema.ts` 的 NICKNAME_MAX_CHARS 一致（按码点计，emoji 算一个） */
export const NICKNAME_MAX_CHARS = 16

// ============================================================================
// 存储（localStorage 在无痕模式下读写都会抛）
// ============================================================================

function memoryStorage(): StorageLike {
  const m = new Map<string, string>()
  return {
    getItem: (k) => m.get(k) ?? null,
    setItem: (k, v) => void m.set(k, v),
    removeItem: (k) => void m.delete(k),
  }
}

export function safeLocalStorage(): StorageLike {
  const ls = (globalThis as { localStorage?: StorageLike }).localStorage
  if (ls == null) return memoryStorage()
  const fallback = memoryStorage()
  return {
    getItem(k) {
      try {
        return ls.getItem(k)
      } catch {
        return fallback.getItem(k)
      }
    },
    setItem(k, v) {
      try {
        ls.setItem(k, v)
      } catch {
        fallback.setItem(k, v)
      }
    },
    removeItem(k) {
      try {
        ls.removeItem(k)
      } catch {
        fallback.removeItem(k)
      }
    },
  }
}

// ============================================================================
// 昵称：纯函数部分
// ============================================================================

/**
 * 控制字符 / 零宽 / 双向覆写：服务端两处清洗（WS `sanitizeText`、HTTP `sanitizeNickname`）的**并集**。
 * ★ 少去一个字符，名字在本地看着合法、存到账号上却被 PATCH /me 整个拒掉（400），永远存不上。
 */
const UNSAFE_CHARS =
  /[\u0000-\u0008\u000B-\u001F\u007F-\u009F\u200B-\u200F\u202A-\u202E\u2060-\u206F\uFEFF]/g

/** 与服务端 `sanitizeNickname` 同口径：去危险字符、空白折叠成一个半角空格、两端 trim。 */
export function sanitizeNickname(raw: string): string {
  return raw.replace(UNSAFE_CHARS, '').trim().replace(/\s+/g, ' ')
}

/** 按**码点**计数：`'👍'.length === 2`，用 length 会把 emoji 算成两个字。 */
export function nicknameLength(raw: string): number {
  return Array.from(raw).length
}

export function truncateNickname(raw: string): string {
  const cps = Array.from(raw)
  return cps.length <= NICKNAME_MAX_CHARS ? raw : cps.slice(0, NICKNAME_MAX_CHARS).join('')
}

/** 返回具体问题；null = 可用。 */
export function nicknameProblem(raw: string): string | null {
  const s = sanitizeNickname(raw)
  if (s === '') return '昵称不能为空'
  if (nicknameLength(s) > NICKNAME_MAX_CHARS) return `昵称最多 ${NICKNAME_MAX_CHARS} 个字`
  return null
}

/** 0..max-1 的随机整数。★ CSPRNG；没有 crypto 时退回时间熵（只影响昵称好不好看）。 */
function randomInt(max: number): number {
  const c = (globalThis as { crypto?: { getRandomValues?: (a: Uint32Array) => Uint32Array } }).crypto
  if (c?.getRandomValues !== undefined) {
    const buf = new Uint32Array(1)
    c.getRandomValues(buf)
    // 拒绝采样：直接取模会让前若干个值多出现一次（红线 #15 的习惯）
    const limit = Math.floor(0x1_0000_0000 / max) * max
    let v = buf[0] ?? 0
    let guard = 0
    while (v >= limit && guard < 8) {
      c.getRandomValues(buf)
      v = buf[0] ?? 0
      guard += 1
    }
    return v % max
  }
  return Date.now() % max
}

/** 「牌手8823」。四位数字：重名概率低到不用管，又短到能一眼记住。 */
export function randomNickname(rand: (max: number) => number = randomInt): string {
  const n = 1000 + rand(9000)
  return `牌手${String(n)}`
}

export function readStoredNickname(store: StorageLike): string | null {
  const raw = store.getItem(NICKNAME_KEY)
  if (raw === null) return null
  const clean = sanitizeNickname(raw)
  return clean === '' ? null : truncateNickname(clean)
}

export function writeStoredNickname(store: StorageLike, nickname: string): void {
  const clean = truncateNickname(sanitizeNickname(nickname))
  if (clean === '') {
    store.removeItem(NICKNAME_KEY)
    store.removeItem(NICKNAME_DIRTY_KEY)
    return
  }
  store.setItem(NICKNAME_KEY, clean)
}

export function readNicknameDirty(store: StorageLike): boolean {
  return store.getItem(NICKNAME_DIRTY_KEY) === '1'
}

export function readNicknameOwner(store: StorageLike): string | null {
  const v = store.getItem(NICKNAME_OWNER_KEY)
  return v === null || v === '' ? null : v
}

function writeNicknameSync(store: StorageLike, dirty: boolean, owner: string | null): void {
  if (dirty) store.setItem(NICKNAME_DIRTY_KEY, '1')
  else store.removeItem(NICKNAME_DIRTY_KEY)
  if (owner === null) store.removeItem(NICKNAME_OWNER_KEY)
  else store.setItem(NICKNAME_OWNER_KEY, owner)
}

/**
 * 服务端给匿名账号起的默认名（`server/src/http/routes.auth.ts` 的 `generateNickname`：
 * `Player-` + 4 位 Crockford 字母，剔掉 I/L/O/U）。这种名字从来不是用户挑的。
 */
const SERVER_DEFAULT_NICKNAME = /^Player-[0-9A-HJKMNP-TV-Z]{4}$/

export function isServerDefaultNickname(name: string): boolean {
  return SERVER_DEFAULT_NICKNAME.test(name)
}

/** `randomNickname` 的产物 */
const RANDOM_NICKNAME = /^牌手[1-9]\d{3}$/

/** 没人挑过的名字：服务端默认名，或客户端随机名 */
export function isPlaceholderNickname(name: string): boolean {
  return isServerDefaultNickname(name) || RANDOM_NICKNAME.test(name)
}

/**
 * - `same`：本地名字和账号名字已经一致。
 * - `take`：改用账号上的名字。
 * - `push`：保留本地名字，并补存到账号上。
 */
export type NicknameVerdict = 'same' | 'take' | 'push'

export interface NicknameSituation {
  /** 本地名字（已归一化） */
  readonly local: string
  /** 账号上的名字（已归一化） */
  readonly account: string
  /** 本地有没存上的改动 */
  readonly dirty: boolean
  /** 还是上次对齐的那个账号（没对齐过的老数据也算） */
  readonly sameIdentity: boolean
}

/**
 * 本地名字和账号名字对不上时，以哪边为准。
 *
 *   1. 本地是空的 → take（账号也空就 same）
 *   2. 两边一样 → same
 *   3. 账号名是空的（不该发生）→ push
 *   4. 同一个身份下，本地有没存上的改动 → push（本地是最新的，比如改名时断网或页面关了）
 *   5. 账号名没人挑过（Player-XXXX / 牌手1234），本地是挑过的名字 → push
 *      （例如 09-15 昵称上云之前只存在本地的名字，或登录到一个从没起过名的账号）
 *   6. 其余情况 → take，以账号为准：登录后换了账号、在别的设备改过名、
 *      修复前登录过的设备名字停在了旧的匿名账号上、两边都是没人挑过的名字
 *
 * ★ 换了身份时忽略 dirty：那条改动属于上一个账号，不能带到新账号上去冲掉它的真名。
 */
export function decideNickname(x: NicknameSituation): NicknameVerdict {
  if (x.local === '') return x.account === '' ? 'same' : 'take'
  if (x.local === x.account) return 'same'
  if (x.account === '') return 'push'
  if (x.dirty && x.sameIdentity) return 'push'
  if (isPlaceholderNickname(x.account) && !isPlaceholderNickname(x.local)) return 'push'
  return 'take'
}

// ============================================================================
// 昵称 store（大厅的三块 UI 分处不同 slot，没法靠 props 共享）
// ============================================================================

interface NicknameStore {
  /** 已归一化、已截断的昵称，随时可直接发出去 */
  readonly nickname: string
  /** 有没存到账号上的改动（见 NICKNAME_DIRTY_KEY） */
  readonly dirty: boolean
  /** 最后一次对齐的账号 id；null = 还没对齐过（首次打开 / 老数据） */
  readonly owner: string | null
  /** 用户亲手改名。存到账号上由调用方负责（`pushNickname`），成功后 `markSynced` */
  setNickname(raw: string): void
  /**
   * 拿到账号（注册、换票、登录换号、资料更新）后对齐本地名字，返回判定结果。
   * 结果是 `push` 时调用方要把本地名字存到账号上 —— 用 `syncNicknameWithAccount` 一步做完。
   *
   * @param change 账号为什么变了。`adopt`（用户主动登录）时，没对齐过的本地名字（owner 为 null）
   *               也按「换了身份」处理：那时的改动不属于这个账号。
   */
  reconcile(account: { readonly id: string; readonly nickname: string }, change?: AuthChange | null): NicknameVerdict
  /**
   * 这个名字在 `accountId` 上已经有了结果（存成功，或被服务端判为不合法、不用再试），
   * 清掉「没存上」标记。期间本地又改过或换了号就不算数。
   */
  markSynced(accountId: string, nickname: string): void
}

function initialNickname(): Pick<NicknameStore, 'nickname' | 'dirty' | 'owner'> {
  const store = safeLocalStorage()
  const stored = readStoredNickname(store)
  if (stored !== null) return { nickname: stored, dirty: readNicknameDirty(store), owner: readNicknameOwner(store) }
  const generated = randomNickname()
  // 随机名也落盘：下次打开还是同一个名字，朋友才认得出是谁
  writeStoredNickname(store, generated)
  writeNicknameSync(store, false, null)
  return { nickname: generated, dirty: false, owner: null }
}

export const useNicknameStore = create<NicknameStore>((set, get) => ({
  ...initialNickname(),
  setNickname: (raw) => {
    const clean = truncateNickname(sanitizeNickname(raw))
    const store = safeLocalStorage()
    writeStoredNickname(store, clean)
    writeNicknameSync(store, clean !== '', get().owner)
    set({ nickname: clean, dirty: clean !== '' })
  },
  reconcile: (account, change) => {
    const s = get()
    const accountName = truncateNickname(sanitizeNickname(account.nickname))
    const sameIdentity = s.owner === account.id || (s.owner === null && change !== 'adopt')
    const verdict = decideNickname({ local: s.nickname, account: accountName, dirty: s.dirty, sameIdentity })
    const store = safeLocalStorage()
    if (verdict === 'take') {
      writeStoredNickname(store, accountName)
      writeNicknameSync(store, false, account.id)
      set({ nickname: accountName, dirty: false, owner: account.id })
      return verdict
    }
    // ★ push 不新添「没存上」标记：只有用户亲手改的名字才算改动。规则 5 的补存失败了，
    //   下次对齐时账号名还是没人挑过的，会再补一次；要是记成改动，别的设备后来起的真名反而会被它盖掉。
    //   换了身份时上一个账号的标记也不带过来。
    const dirty = verdict === 'push' && sameIdentity && s.dirty
    writeNicknameSync(store, dirty, account.id)
    set({ dirty, owner: account.id })
    return verdict
  },
  markSynced: (accountId, nickname) => {
    const s = get()
    if (s.owner !== accountId || s.nickname !== nickname || !s.dirty) return
    writeNicknameSync(safeLocalStorage(), false, accountId)
    set({ dirty: false })
  },
}))

/** 当前昵称（非 React 上下文里用，例如接线层）。 */
export function currentNickname(): string {
  return useNicknameStore.getState().nickname
}

// ============================================================================
// AuthSession 单例
// ============================================================================

let session: AuthSession | null = null

/**
 * 进程内唯一的身份会话。接线层拿它去喂 WsTransport：
 *   `createWsTransport(wsUrl, () => auth.ensureAccessToken(), { refreshToken: () => auth.forceRefresh() })`
 */
export function getAuthSession(options?: AuthSessionOptions): AuthSession {
  if (session === null) {
    // 匿名注册时把本地名字带上，账号名从一开始就和本地一致（否则服务端起个 Player-XXXX，
    // 大厅输入框会在打开后一两秒自己从「牌手1234」跳成英文名）
    session = createAuthSession({ nickname: currentNickname, ...options })
    // ★ 账号每变一次（注册、换票、登录、资料更新）都对齐一次名字，不只在大厅挂载时：
    //   锁屏回来 WS 重连换票、会话作废后顶了新号、改头像带回别的设备改的名字，都走这里
    watchAccountNickname(session)
  }
  return session
}

/** 测试与接线层注入用；传 null 清空单例。 */
export function setAuthSession(next: AuthSession | null): void {
  session = next
}

export type AuthStatus = 'idle' | 'working' | 'ready' | 'error'

/**
 * 确保有一个匿名账号可用。重复调用是安全的（`ensureAccessToken` 内部单飞）。
 * 返回 null 表示这次没拿到（网络问题），调用方应该允许重试。
 */
export async function ensureAnonymousAccount(): Promise<PublicAccount | null> {
  const auth = getAuthSession()
  try {
    await auth.ensureAccessToken()
    return auth.account()
  } catch {
    return null
  }
}

export interface AuthView {
  readonly account: PublicAccount | null
  readonly deviceId: string
  readonly status: AuthStatus
  /** 面向用户的一句话；null = 没出错 */
  readonly error: string | null
  readonly nickname: string
  setNickname(raw: string): void
  /** 注册/刷新失败后手动重试 */
  retry(): void
}

/**
 * 首屏就调它。**没有任何表单**：挂载即在后台把身份准备好。
 *
 * 失败不阻塞界面：`status === 'error'` 时大厅照常显示，只是把 CTA 置灰并给一行
 * 「连不上服务器，点这里重试」。把网络错误做成全屏模态是最劝退的做法。
 */
export function useAuth(): AuthView {
  const auth = getAuthSession()
  const nickname = useNicknameStore((s) => s.nickname)
  const setNickname = useNicknameStore((s) => s.setNickname)

  const [account, setAccount] = useState<PublicAccount | null>(() => auth.account())
  const [status, setStatus] = useState<AuthStatus>(() => (auth.account() === null ? 'idle' : 'ready'))
  const [error, setError] = useState<string | null>(null)
  const [attempt, setAttempt] = useState(0)

  // 账号变化（注册成功、刷新换号）推回组件
  useEffect(() => auth.onChange((s) => setAccount(s.account)), [auth])

  useEffect(() => {
    let alive = true
    if (auth.account() !== null && attempt === 0) {
      setStatus('ready')
      return () => {
        alive = false
      }
    }
    setStatus('working')
    setError(null)
    // 名字的对齐不在这里做：账号一落地，getAuthSession 挂的监听就同步对齐了（不跟组件生命周期走）
    auth
      .ensureAccessToken()
      .then(() => {
        if (!alive) return
        setAccount(auth.account())
        setStatus('ready')
      })
      .catch((e: unknown) => {
        if (!alive) return
        setError(e instanceof Error ? e.message : '连不上服务器')
        setStatus('error')
      })
    return () => {
      alive = false
    }
  }, [auth, attempt])

  const retry = useCallback(() => setAttempt((n) => n + 1), [])

  return { account, deviceId: auth.deviceId(), status, error, nickname, setNickname, retry }
}
